import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * The cached pages of an infinite query, cut to the first one. An updater for `setQueryData`.
 *
 * A refetch of an infinite query reads every cached page again, in sequence. Cutting the cache
 * first is what makes the read that follows a read of one page.
 */
export const keepFirstPage = (
  old: InfiniteData<unknown> | undefined,
): InfiniteData<unknown> | undefined =>
  old ? { pages: old.pages.slice(0, 1), pageParams: old.pageParams.slice(0, 1) } : old;

/**
 * Cancel in-flight fetches, truncate cache to page 1, then re-fetch.
 *
 * Shared by infinite query hooks that need a "pull-to-refresh" style reset:
 * drops all but the first cached page so the UI doesn't jump, then
 * invalidates to trigger a clean re-fetch of page 1.
 *
 * Resolves with nothing, and at once when the fetch is parked (the app believes it is offline),
 * so a pull-to-refresh spinner that awaits it ends. A retry control needs the outcome of the
 * read instead, and reads in the same tick as it cuts: see `refetchFirstPage` in
 * useInfiniteSpaceQuestions.
 */
export async function refreshInfiniteQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
): Promise<void> {
  await queryClient.cancelQueries({ queryKey, exact: true });
  queryClient.setQueryData<InfiniteData<unknown>>(queryKey, keepFirstPage);
  await queryClient.invalidateQueries({ queryKey, exact: true });
}
